import { z } from 'zod';

import { zMoney } from './common';

/**
 * Биллинг платформы.
 *
 * Это НЕ деньги организации. Кошельки, операции и выплаты воркспейса живут в
 * `dto/finance.ts` и к балансу EsportCRM отношения не имеют: там организация
 * ведёт свои деньги, здесь пользователь платит за саму платформу. Два контура
 * не пересекаются ни моделью, ни кодом, и смешивать их нельзя даже «на время».
 *
 * Второй инвариант, ради которого написан весь этот модуль:
 *   баланс никогда не хранится числом. Он вычисляется суммой неизменяемых
 *   записей журнала в текущей валюте аккаунта — как и деньги организаций.
 *
 * Третий: баланс всегда `>= 0`. Кредита, рассрочки и овердрафта в EsportCRM
 * не существует, и штатной операции, уводящей аккаунт в минус, тоже.
 */

/* ─── Регион и валюта ───────────────────────────────────────────────────── */

/**
 * Коды стран ISO 3166-1 alpha-2.
 *
 * Список нужен, чтобы отвергать выдуманные коды: регион — финансовый признак,
 * и `countryCode: 'XX'` не должен доезжать до базы. Названия стран здесь не
 * хранятся: интерфейс берёт локализованное имя через `Intl.DisplayNames`,
 * иначе пришлось бы вести двести названий на каждом языке вручную.
 */
export const COUNTRY_CODES: readonly string[] = (
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR ' +
  'BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ ' +
  'EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW ' +
  'GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY ' +
  'KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV ' +
  'MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY ' +
  'QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG ' +
  'TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'
).split(' ');

const COUNTRY_SET = new Set(COUNTRY_CODES);

export function isKnownCountry(code: string): boolean {
  return COUNTRY_SET.has(code);
}

/** Валюты, в которых может вестись баланс аккаунта. */
export const BILLING_CURRENCIES = ['RUB', 'USD'] as const;
export type BillingCurrency = (typeof BILLING_CURRENCIES)[number];

/**
 * Валюта аккаунта выводится из региона и отдельно не выбирается.
 *
 * Выбор валюты пользователем породил бы аккаунты, где регион и валюта
 * противоречат друг другу, и каждый такой аккаунт пришлось бы разбирать
 * руками. Правило одно: Россия — рубль, всё остальное — доллар.
 */
export function billingCurrencyFor(countryCode: string): BillingCurrency {
  return countryCode === 'RU' ? 'RUB' : 'USD';
}

export const zCountryCode = z
  .string()
  .trim()
  .toUpperCase()
  .length(2)
  .refine(isKnownCountry, { message: 'validation.unknownCountry' });

/* ─── Состояние аккаунта ────────────────────────────────────────────────── */

/**
 * `SETUP_REQUIRED` — регион не выбран. Так заводятся аккаунты пользователей,
 * зарегистрированных до появления биллинга: угадывать их регион по языку,
 * часовому поясу или адресу нельзя, это дало бы ложные финансовые данные.
 */
export const BILLING_ACCOUNT_STATUSES = ['SETUP_REQUIRED', 'ACTIVE', 'SUSPENDED'] as const;
export type BillingAccountStatus = (typeof BILLING_ACCOUNT_STATUSES)[number];

/**
 * Регион demo-аккаунта — единственный источник, не дублировать `'RU'`
 * по модулям (TASK #34 PHASE 3).
 *
 * Demo-аккаунт никогда не может выбрать регион сам: billing write-маршруты,
 * включая `setupRegion`, заперты для demo через `denyDemo`. Поэтому у demo
 * всегда один и тот же фиксированный регион, назначаемый системой, а не
 * пользователем, — не полноценный выбор, а константа, которая нужна только
 * для того, чтобы demo-аккаунт вообще не мог застрять в `SETUP_REQUIRED`.
 */
export const DEMO_BILLING_COUNTRY_CODE = 'RU';

/* ─── Журнал ────────────────────────────────────────────────────────────── */

/**
 * Типы записей журнала.
 *
 * Часть из них пока не создаётся ни одним сценарием — они заложены, чтобы
 * подписки и рефералы легли на тот же баланс, а не завели себе второй.
 *
 * `REFUND` и `CHARGEBACK` здесь отсутствуют намеренно: возврат средств
 * пользователю EsportCRM не предоставляет, а поведение внешних отмен платежа
 * будет спроектировано отдельно, когда появится конкретный процессинг.
 */
export const BILLING_ENTRY_TYPES = [
  'TOP_UP',
  'SUBSCRIPTION_CHARGE',
  'ADMIN_CREDIT',
  'ADMIN_DEBIT',
  'REFERRAL_CREDIT',
  'REFERRAL_REVERSAL',
  'CURRENCY_CONVERSION_OUT',
  'CURRENCY_CONVERSION_IN',
  'REVERSAL',
] as const;
export type BillingEntryType = (typeof BILLING_ENTRY_TYPES)[number];

/**
 * Верхняя граница баланса — своя для каждой валюты.
 *
 * Одно число на все валюты означало бы, что миллион рублей и миллион долларов
 * — одна и та же граница; они отличаются примерно в сто раз, и какая из двух
 * окажется неверной, зависит только от того, под какую валюту подбирали цифру.
 * Поэтому политика задаётся по валютам и в минорных единицах, как и все
 * остальные деньги в проекте.
 *
 * Граница нужна отдельно от предельной суммы одной операции: накопить
 * неограниченно много денег на балансе сервиса нельзя ни технически, ни —
 * когда дойдёт до настоящего процессинга — юридически.
 */
export const MAX_BILLING_BALANCE_MINOR = {
  /** 1 000 000,00 ₽ */
  RUB: 100_000_000n,
  /** 10 000,00 $ */
  USD: 1_000_000n,
} satisfies Record<BillingCurrency, bigint>;

/**
 * Предел для валюты. Для незнакомой валюты — отказ, а не значение по умолчанию.
 *
 * Запасное значение здесь было бы худшим из вариантов: новая валюта прошла бы
 * молча, с границей, подобранной под чужие деньги. Это нарушение инварианта, а
 * не ошибка пользователя, поэтому и выражено обычным исключением: чинить надо
 * код, добавив валюту в политику.
 */
export function maxBillingBalanceFor(currency: string): bigint {
  const limit = (MAX_BILLING_BALANCE_MINOR as Record<string, bigint | undefined>)[currency];

  if (limit === undefined) {
    throw new Error(`Предельный баланс для валюты ${currency} не задан`);
  }

  return limit;
}

/* ─── DTO ───────────────────────────────────────────────────────────────── */

export interface BillingAccountDto {
  countryCode: string | null;
  currency: BillingCurrency | null;
  balanceMinor: number;
  status: BillingAccountStatus;
  /** Регион ещё не выбран — интерфейс просит выбрать его до любых операций. */
  setupRequired: boolean;
  regionChangedAt: string | null;
  /**
   * Когда станет доступна следующая смена региона. `null` — смен ещё не было
   * и ждать нечего.
   */
  nextRegionChangeAllowedAt: string | null;
}

export interface BillingEntryDto {
  id: string;
  type: BillingEntryType;
  amountMinor: number;
  currency: string;
  description: string | null;
  createdAt: string;
  /** Заполнено у записи, которая отменяет другую. */
  reversesEntryId: string | null;
}

export interface BillingHistoryResponse {
  items: BillingEntryDto[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

/** Данные аккаунта для администратора платформы. */
export interface AdminBillingAccountDto extends BillingAccountDto {
  userId: string;
  username: string;
  displayName: string;
}

/* ─── Схемы запросов ────────────────────────────────────────────────────── */

/**
 * Ограничение частоты смены региона.
 *
 * Регион — не настройка темы: он задаёт валюту расчётов, и каждая смена
 * конвертирует весь остаток по официальному курсу. Свободное переключение
 * туда-сюда — это и игра на курсовой разнице, и постоянно меняющаяся валюта
 * аккаунта. Тридцать суток отмеряются от фактической последней успешной
 * смены, а не от отметки в аккаунте.
 *
 * Первоначальный выбор региона (`setupRegion`) сменой не считается и окно не
 * расходует: менять там было нечего.
 */
export const REGION_CHANGE_COOLDOWN_DAYS = 30;
export const REGION_CHANGE_COOLDOWN_MS = REGION_CHANGE_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;

/**
 * Смена региона.
 *
 * Ни аккаунта, ни даты курса клиент не передаёт: аккаунт берётся из сессии,
 * дату для официального курса определяет сервер. Иначе выбором даты можно
 * было бы подобрать выгодный курс.
 */
export const billingChangeRegionSchema = z.object({
  countryCode: zCountryCode,
  /** Повтор запроса с тем же ключом возвращает уже проведённую операцию. */
  idempotencyKey: z.string().trim().min(8).max(128),
});
export type BillingChangeRegionInput = z.infer<typeof billingChangeRegionSchema>;

/** Что видит клиент после успешной смены региона. */
export interface BillingRegionChangeResponse {
  account: BillingAccountDto;
  operation: {
    operationId: string;
    fromCountryCode: string;
    toCountryCode: string;
    fromCurrency: BillingCurrency;
    toCurrency: BillingCurrency;
    /** Остаток до и после конвертации; равны нулю, когда конвертировать было нечего. */
    sourceAmountMinor: number;
    targetAmountMinor: number;
    /** Менялась ли валюта: при смене страны внутри одной валюты — нет. */
    converted: boolean;
    /** Снимок официального курса, если конвертация была. */
    fxRateSnapshotId: string | null;
  };
}

export const billingSetupRegionSchema = z.object({
  countryCode: zCountryCode,
});
export type BillingSetupRegionInput = z.infer<typeof billingSetupRegionSchema>;

export const billingHistoryQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export type BillingHistoryQuery = z.infer<typeof billingHistoryQuerySchema>;

/**
 * Корректировка баланса администратором.
 *
 * Валюту клиент не задаёт: она берётся из аккаунта. Причина обязательна —
 * через год на вопрос «почему пользователю начислили 300 долларов» ответ
 * должен находиться в системе, а не в чьей-то памяти.
 */
export const adminBillingAdjustSchema = z.object({
  amountMinor: zMoney,
  reason: z.string().trim().min(3).max(500),
  /** Повторный запрос с тем же ключом не создаёт вторую операцию. */
  idempotencyKey: z.string().trim().min(8).max(128),
});
export type AdminBillingAdjustInput = z.infer<typeof adminBillingAdjustSchema>;

export const adminBillingReverseSchema = z.object({
  reason: z.string().trim().min(3).max(500),
  idempotencyKey: z.string().trim().min(8).max(128),
});
export type AdminBillingReverseInput = z.infer<typeof adminBillingReverseSchema>;
